import React from "react";
import { Palette } from "../holidayConfig";
import { mix } from "./kit";
import { range } from "./sceneUtils";
import { Blush, Contact, Eye, Shaded, ShadeColors, Sphere } from "./toon";

/*
 * Luciatåget: Lucia med ljuskrona, tärnor med ljus i händerna och
 * stjärngossar med strut och stjärnstav. Barnen ser olika ut (hudton,
 * hår, frisyr) och har olika uttryck: några sjunger, några ler med
 * stängd mun, någon blundar och någon tittar ner i sitt ljus. Levande
 * ljus är det enda varma ljuset, så ansikten och särkar lyses upp
 * underifrån av lågorna. Tåget går åt höger, mot änkehuset.
 */

export const SKINS = {
  fair: { base: "#ffdcc8", shade: "#ecb49c", light: "#fff3ea", rim: "#f5c8b2" },
  light: { base: "#f9d2b4", shade: "#e2a886", light: "#ffeadb", rim: "#eebc9c" },
  golden: { base: "#f0c79c", shade: "#d19e72", light: "#fbe1c4", rim: "#e0b088" },
  olive: { base: "#d9a47a", shade: "#b47b52", light: "#ecc49f", rim: "#c98f66" },
  deep: { base: "#8d5a3b", shade: "#653b23", light: "#b07b55", rim: "#7a4a2f" },
} as const;

export type SkinKey = keyof typeof SKINS;

/** Kinder, läppar och munhåla som passar hudtonen. */
const TONE: Record<SkinKey, { blush: string; blushOpacity: number; lip: string; mouth: string }> = {
  fair: { blush: "#ff9a92", blushOpacity: 0.55, lip: "#b4435a", mouth: "#8e2f45" },
  light: { blush: "#ff958a", blushOpacity: 0.5, lip: "#ad4256", mouth: "#8a2e42" },
  golden: { blush: "#f59a86", blushOpacity: 0.45, lip: "#b0475a", mouth: "#86303f" },
  olive: { blush: "#e08470", blushOpacity: 0.4, lip: "#93394a", mouth: "#702634" },
  deep: { blush: "#c1574e", blushOpacity: 0.35, lip: "#5e2025", mouth: "#43151b" },
};

export const HAIR = {
  blonde: { base: "#f6d98a", shade: "#d3a94e", light: "#fff2c6" },
  brown: { base: "#9a6440", shade: "#643a22", light: "#c99270" },
  red: { base: "#e08a4c", shade: "#a8552a", light: "#f7bd8c" },
  dark: { base: "#4a3328", shade: "#241812", light: "#7a5a4a" },
  black: { base: "#231e2a", shade: "#0d0a12", light: "#55506c" },
  afro: { base: "#2e1f18", shade: "#150d09", light: "#5f4337" },
} as const;

export type HairKey = keyof typeof HAIR;

/**
 * long   – långt rakt hår
 * braids – två flätor över axlarna
 * puffs  – två afropuffar
 * bangs  – rakt hår med rak lugg
 * short  – kort hår under struten
 * curly  – lockigt hår under struten
 */
export type HairStyle = "long" | "braids" | "puffs" | "bangs" | "short" | "curly";

/** open = tittar fram mot Lucia, soft = halvslutna, down = tittar i ljuset, closed = blundar och ler. */
export type EyeMood = "open" | "soft" | "down" | "closed";

/** smile = stängd mun, o = sjunger ett litet o, sing = sjunger, grin = busigt flin. */
export type MouthMood = "smile" | "o" | "sing" | "grin";

export interface Look {
  skin: SkinKey;
  hair: HairKey;
  style: HairStyle;
  eyes: EyeMood;
  mouth: MouthMood;
  iris?: string;
  freckles?: boolean;
  /** Sekunder, så att barnen inte blinkar i takt. */
  blink?: number;
}

const SASH: ShadeColors = { base: "#d8263f", shade: "#8f1226", light: "#ff6b7d", rim: "#b81c34" };
const WAX: ShadeColors = { base: "#ffffff", shade: "#d8dcf2", light: "#ffffff" };
const LEAF: ShadeColors = { base: "#3f8a4a", shade: "#1f5230", light: "#7cc27f" };
const GOLD: ShadeColors = { base: "#ffd76a", shade: "#d9a227", light: "#fff4c4" };
const LASH = "#1d1630";
/** Huvudet vrids lite åt höger, i gångriktningen. */
const TURN = 2.2;

const gown = (p: Palette): ShadeColors => ({ base: "#ffffff", shade: mix("#e3e6f8", p[300], 0.25), light: "#ffffff", rim: "#f1f2fc" });

/** Levande ljuslåga med glöd. (x, y) = vekens topp. */
export const Flame: React.FC<{ x: number; y: number; s?: number; delay?: number; halo?: number }> = ({ x, y, s = 1, delay = 0, halo = 22 }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <circle r={halo} cy={-5} fill="url(#lu-halo)" className="hol-flicker" style={{ animationDelay: `${delay}s` }} />
    <g className="hol-candle-flame" style={{ animationDelay: `${delay}s` }}>
      <path d="M0 -12 C 4 -7, 3.6 -1.5, 0 0 C -3.6 -1.5, -4 -7, 0 -12 Z" fill="url(#lu-flame)" />
      <path d="M0 -6 C 1.5 -4, 1.4 -1.2, 0 -0.6 C -1.4 -1.2, -1.5 -4, 0 -6 Z" fill="#ffffff" />
    </g>
    <path d="M0 0 V 2.6" stroke="#2b2030" strokeWidth="1" />
  </g>
);

/** Ett stearinljus. (x, y) = ljusets fot, h = höjd. */
const Candle: React.FC<{ x: number; y: number; h: number; w?: number; delay?: number; halo?: number }> = ({ x, y, h, w = 5, delay = 0, halo }) => (
  <g>
    <Shaded d={`M${x - w / 2} ${y} V ${y - h} C ${x - w / 4} ${y - h + 1.5}, ${x + w / 4} ${y - h + 1.5}, ${x + w / 2} ${y - h} V ${y} Z`} c={WAX} depth={1.6} />
    <path d={`M${x + w * 0.18} ${y - h + 1} q ${w * 0.3} ${h * 0.2} 0 ${h * 0.34}`} stroke="#ffffff" strokeWidth="1.2" fill="none" strokeLinecap="round" />
    <Flame x={x} y={y - h - 2.6} s={w / 5} delay={delay} halo={halo} />
  </g>
);

/* ---------- Hår ---------- */

/** Hår som ligger bakom kroppen. */
const BackHair: React.FC<{ look: Look }> = ({ look }) => {
  const h = HAIR[look.hair];
  switch (look.style) {
    case "long":
      return <Shaded d="M-22 -152 C -32 -124, -32 -104, -26 -92 L 26 -92 C 32 -104, 32 -124, 22 -152 Z" c={h} depth={5} />;
    case "bangs":
      return <Shaded d="M-23 -150 C -26 -130, -26 -112, -25 -98 L 25 -98 C 26 -112, 26 -130, 23 -150 Z" c={h} depth={5} />;
    case "braids":
      return <Shaded d="M-21 -150 C -26 -134, -25 -122, -21 -114 L 21 -114 C 25 -122, 26 -134, 21 -150 Z" c={h} depth={4} />;
    case "puffs":
      return (
        <g>
          {[-1, 1].map(side => (
            <g key={side}>
              <Sphere cx={side * 19} cy={-165} r={12.5} c={h} />
              {range(9).map(i => {
                const a = (i / 9) * Math.PI * 2;
                return <circle key={i} cx={side * 19 + Math.cos(a) * 7.5} cy={-165 + Math.sin(a) * 7.5} r={1.4} fill={i % 2 ? h.light : h.shade} opacity="0.45" />;
              })}
            </g>
          ))}
        </g>
      );
    default:
      return null;
  }
};

/** En fläta som en kedja av korta, snedställda ovaler. */
const Braid: React.FC<{ side: number; h: ShadeColors }> = ({ side, h }) => (
  <g>
    {range(6).map(i => {
      const bx = side * (18.5 + i * 0.5);
      const by = -134 + i * 6;
      return (
        <g key={i}>
          <ellipse cx={bx} cy={by} rx={4.6 - i * 0.25} ry={3.8} transform={`rotate(${(i % 2 ? 28 : -28) * side} ${bx} ${by})`} fill={h.base} />
          <path d={`M${bx - 3} ${by + 1.6} Q${bx} ${by + 3.4} ${bx + 3} ${by + 1.6}`} stroke={h.shade} strokeWidth="1" fill="none" opacity="0.8" />
        </g>
      );
    })}
    {/* Röd sammetsrosett längst ner */}
    <path d={`M${side * 20.8} ${-97} l${-4.4} -2.6 v5.2 Z M${side * 20.8} ${-97} l4.4 -2.6 v5.2 Z`} fill={SASH.base} />
    <circle cx={side * 20.8} cy={-97} r={1.4} fill={SASH.shade} />
  </g>
);

/** Hår som ligger framför huvudet (lugg, slingor, flätor). */
const FrontHair: React.FC<{ look: Look }> = ({ look }) => {
  const h = HAIR[look.hair];
  switch (look.style) {
    case "long":
      return (
        <g>
          <Shaded d="M-21 -142 C -24 -164, -10 -170, 0 -170 C 12 -170, 24 -162, 21 -142 C 16 -152, 8 -158, -2 -156 C -10 -154, -16 -150, -21 -142 Z" c={h} depth={4} />
          <Shaded d="M-20 -148 C -27 -132, -25 -116, -19 -104 L -13 -107 C -17 -120, -17 -134, -15 -146 Z" c={h} depth={3} />
          <Shaded d="M20 -148 C 27 -132, 25 -116, 19 -104 L 13 -107 C 17 -120, 17 -134, 15 -146 Z" c={h} depth={3} />
          <path d="M-11 -163 Q -2 -167 8 -164" stroke={h.light} strokeWidth="2" fill="none" strokeLinecap="round" opacity="0.7" />
        </g>
      );
    case "braids":
      return (
        <g>
          {/* Sidbena med luggen svept åt höger */}
          <Shaded d="M-21 -141 C -24 -163, -9 -171, 2 -170 C 14 -169, 23 -160, 21 -141 C 19 -150, 15 -155, 9 -157 C 2 -151, -10 -149, -21 -141 Z" c={h} depth={4} />
          <path d="M-9 -165 Q 0 -168 10 -164" stroke={h.light} strokeWidth="1.8" fill="none" strokeLinecap="round" opacity="0.7" />
          <Braid side={-1} h={h} />
          <Braid side={1} h={h} />
        </g>
      );
    case "puffs":
      return (
        <g>
          {/* Tätt, krusigt hår mot hjässan med mjukt bucklig hårfäste */}
          <Shaded d="M-20.6 -141 C -22.6 -161, -12 -168, 0 -168 C 12 -168, 22.6 -161, 20.6 -141 C 18 -149, 11 -154.6, 0 -154.6 C -11 -154.6, -18 -149, -20.6 -141 Z" c={h} depth={3} />
          {range(11).map(i => {
            const u = i / 10;
            const a = Math.PI + u * Math.PI;
            return <circle key={i} cx={Math.cos(a) * 19.4} cy={-147.5 + Math.sin(a) * 7.6} r={2} fill={h.base} />;
          })}
          {range(8).map(i => (
            <circle key={`t${i}`} cx={-12 + i * 3.4} cy={-162 + (i % 2) * 2.2} r={1} fill={h.light} opacity="0.4" />
          ))}
        </g>
      );
    case "bangs":
      return (
        <g>
          {/* Rak, blank lugg och raka sidor */}
          <Shaded d="M-22.4 -140 C -24 -165, -11 -171, 0 -171 C 11 -171, 24 -165, 22.4 -140 L 20 -140 C 19.6 -144, 19.4 -147, 19 -149.4 L -19 -149.4 C -19.4 -147, -19.6 -144, -20 -140 Z" c={h} depth={4} />
          <Shaded d="M-22.4 -146 C -24 -132, -23.6 -120, -21.6 -108 L -15.6 -109 C -16.6 -122, -17 -134, -17 -146 Z" c={h} depth={2.4} />
          <Shaded d="M22.4 -146 C 24 -132, 23.6 -120, 21.6 -108 L 15.6 -109 C 16.6 -122, 17 -134, 17 -146 Z" c={h} depth={2.4} />
          <path d="M-13 -163.6 Q 0 -167.6 13 -163.6" stroke={h.light} strokeWidth="2.4" fill="none" strokeLinecap="round" opacity="0.65" />
          {range(7).map(i => (
            <path key={i} d={`M${-15 + i * 5} -157 V-150`} stroke={h.shade} strokeWidth="0.7" opacity="0.5" />
          ))}
        </g>
      );
    case "short":
      return (
        <g>
          <Shaded d="M-20.4 -144 C -19 -154, -10 -157, 0 -156 C 10 -157, 19 -154, 20.4 -144 C 15 -150, 8 -150.6, 3 -149 L 0 -152 L -3 -149 C -9 -150.6, -15 -150, -20.4 -144 Z" c={h} depth={2} />
          <path d="M-20 -146 q -1.2 4 0.4 8 M20 -146 q 1.2 4 -0.4 8" stroke={h.base} strokeWidth="2.6" strokeLinecap="round" fill="none" />
        </g>
      );
    case "curly":
      return (
        <g>
          {range(9).map(i => {
            const cx = -18 + i * 4.5;
            return <Sphere key={i} cx={cx} cy={-150.5 + Math.abs(i - 4) * 0.6} r={3.2} c={h} />;
          })}
          {[-1, 1].map(side => (
            <g key={side}>
              <Sphere cx={side * 20.4} cy={-145} r={3} c={h} />
              <Sphere cx={side * 20.8} cy={-139.5} r={2.6} c={h} />
            </g>
          ))}
        </g>
      );
    default:
      return null;
  }
};

/** Glitter (lametta) i håret. */
const Tinsel: React.FC<{ y: number; w: number }> = ({ y, w }) => {
  const d = `M${-w} ${y + 4} C ${-w * 0.6} ${y - 2}, ${-w * 0.3} ${y + 6}, 0 ${y - 1} C ${w * 0.3} ${y + 6}, ${w * 0.6} ${y - 2}, ${w} ${y + 4}`;
  return (
    <g>
      <path d={d} stroke="#e8ecff" strokeWidth="2.8" fill="none" strokeLinecap="round" />
      <path d={d} stroke="#ffffff" strokeWidth="1.1" strokeDasharray="1 3" fill="none" className="hol-star-group" />
    </g>
  );
};

/* ---------- Ansikte ---------- */

const Face: React.FC<{ look: Look; brows: boolean }> = ({ look, brows }) => {
  const sk = SKINS[look.skin];
  const tone = TONE[look.skin];
  const hair = HAIR[look.hair];
  const iris = look.iris ?? "#3a2a22";
  const tx = TURN;
  const ey = -142;
  const xs = [-7.2 + tx, 7.2 + tx];
  const rx = 3.6;
  const ry = 4.4;
  const singing = look.mouth === "o" || look.mouth === "sing";

  /** Ögonlock som täcker övre delen av ögat, med fransrad i underkanten. */
  const lid = (x: number, edge: number) => (
    <g key={`lid${x}`}>
      <path d={`M${x - rx - 0.6} ${edge} A${rx + 0.6} ${ry + 0.6} 0 0 1 ${x + rx + 0.6} ${edge} Q${x} ${edge + 1.6} ${x - rx - 0.6} ${edge} Z`} fill={sk.shade} />
      <path d={`M${x - rx - 0.6} ${edge} Q${x} ${edge + 1.6} ${x + rx + 0.6} ${edge}`} stroke={LASH} strokeWidth="1.3" fill="none" strokeLinecap="round" />
      <path d={`M${x + (x > tx ? rx : -rx)} ${edge - 0.2} l${x > tx ? 1.6 : -1.6} -1.2`} stroke={LASH} strokeWidth="1" strokeLinecap="round" />
    </g>
  );

  let eyes: React.ReactNode;
  if (look.eyes === "closed") {
    eyes = xs.map(x => (
      <g key={x}>
        <path d={`M${x - 3.4} ${ey - 0.4} Q${x} ${ey + 2.8} ${x + 3.4} ${ey - 0.4}`} stroke={LASH} strokeWidth="1.5" fill="none" strokeLinecap="round" />
        <path d={`M${x + (x > tx ? 3.4 : -3.4)} ${ey - 0.4} l${x > tx ? 1.4 : -1.4} -1`} stroke={LASH} strokeWidth="1" strokeLinecap="round" />
      </g>
    ));
  } else {
    const gaze: [number, number] = look.eyes === "down" ? [0.05, 0.5] : look.eyes === "soft" ? [0.2, 0.2] : [0.32, -0.12];
    eyes = (
      <g className="hol-blink" style={{ animationDelay: `${look.blink ?? 0}s` }}>
        {xs.map(x => (
          <Eye key={x} x={x} y={ey} rx={rx} ry={ry} iris={iris} look={gaze} />
        ))}
        {look.eyes === "soft" && xs.map(x => lid(x, ey - 1.4))}
        {look.eyes === "down" && xs.map(x => lid(x, ey - 2.2))}
        {look.eyes === "open" &&
          xs.map(x => <path key={`l${x}`} d={`M${x + (x > tx ? rx - 0.4 : -rx + 0.4)} ${ey - ry * 0.55} l${x > tx ? 1.6 : -1.6} -1.3`} stroke={LASH} strokeWidth="1" strokeLinecap="round" />)}
      </g>
    );
  }

  const mx = tx * 0.9;
  const my = -130.2;
  let mouth: React.ReactNode;
  switch (look.mouth) {
    case "o":
      mouth = (
        <g>
          <ellipse cx={mx} cy={my} rx="2.2" ry="2.7" fill={tone.mouth} />
          <ellipse cx={mx} cy={my + 1.2} rx="1.3" ry="0.9" fill="#ff8a9a" opacity="0.8" />
        </g>
      );
      break;
    case "sing":
      mouth = (
        <g>
          <path d={`M${mx - 4} ${my - 1} Q${mx} ${my - 0.2} ${mx + 4} ${my - 1} Q${mx + 3.2} ${my + 4.6} ${mx} ${my + 4.8} Q${mx - 3.2} ${my + 4.6} ${mx - 4} ${my - 1} Z`} fill={tone.mouth} />
          <path d={`M${mx - 2.4} ${my + 3.4} Q${mx} ${my + 1.6} ${mx + 2.4} ${my + 3.4} Q${mx} ${my + 4.8} ${mx - 2.4} ${my + 3.4} Z`} fill="#ff8a9a" />
          <path d={`M${mx - 3.2} ${my - 0.6} Q${mx} ${my + 0.4} ${mx + 3.2} ${my - 0.6}`} stroke="#ffffff" strokeWidth="1" fill="none" opacity="0.9" />
        </g>
      );
      break;
    case "grin":
      mouth = (
        <g>
          <path d={`M${mx - 5.4} ${my - 1.4} Q${mx} ${my + 0.2} ${mx + 5.4} ${my - 1.4} Q${mx + 4} ${my + 4} ${mx} ${my + 4.2} Q${mx - 4} ${my + 4} ${mx - 5.4} ${my - 1.4} Z`} fill={tone.mouth} />
          <path d={`M${mx - 4.4} ${my - 0.8} Q${mx} ${my + 0.6} ${mx + 4.4} ${my - 0.8} L${mx + 3.8} ${my + 1} Q${mx} ${my + 2} ${mx - 3.8} ${my + 1} Z`} fill="#ffffff" />
          <path d={`M${mx + 5.4} ${my - 1.4} q 1 -0.6 1.4 -1.6`} stroke={tone.lip} strokeWidth="1" strokeLinecap="round" fill="none" />
        </g>
      );
      break;
    default:
      mouth = (
        <g>
          <path d={`M${mx - 4} ${my - 1} Q${mx} ${my + 2.6} ${mx + 4} ${my - 1}`} stroke={tone.lip} strokeWidth="1.6" fill="none" strokeLinecap="round" />
          <path d={`M${mx - 4.6} ${my - 1.6} q 0.4 0.4 0.8 0.8 M${mx + 4.6} ${my - 1.6} q -0.4 0.4 -0.8 0.8`} stroke={tone.lip} strokeWidth="0.9" strokeLinecap="round" opacity="0.7" />
        </g>
      );
  }

  const by = singing ? -151.2 : -149.8;
  return (
    <g>
      {brows &&
        xs.map(x => (
          <path
            key={`b${x}`}
            d={`M${x - 3.4} ${by + 0.6} Q${x} ${by - 1.6} ${x + 3.4} ${by + 0.4}`}
            stroke={look.hair === "blonde" ? "#c09346" : hair.shade}
            strokeWidth="1.4"
            fill="none"
            strokeLinecap="round"
            opacity="0.85"
          />
        ))}
      {eyes}
      {/* Liten näsa: skugga och högdager */}
      <ellipse cx={tx + 0.8} cy={-135.6} rx="1.7" ry="1.2" fill={sk.shade} opacity="0.8" />
      <circle cx={tx + 0.2} cy={-136.2} r="0.6" fill={sk.light} opacity="0.9" />
      <Blush x={-10.6 + tx} y={-133} r={4.2} color={tone.blush} opacity={tone.blushOpacity} />
      <Blush x={10.6 + tx} y={-133} r={4.2} color={tone.blush} opacity={tone.blushOpacity} />
      {look.freckles &&
        [-1, 1].map(side =>
          [
            [0, 0],
            [2.4, 0.8],
            [1.2, -1.6],
            [-1.6, -0.6],
          ].map(([fx, fy], i) => <circle key={`${side}${i}`} cx={side * 10.4 + tx + fx} cy={-134.4 + fy} r="0.55" fill={sk.shade} />)
        )}
      {mouth}
    </g>
  );
};

/* ---------- Kropp ---------- */

interface ChildProps {
  x: number;
  y: number;
  s?: number;
  p: Palette;
  look: Look;
  delay?: number;
}

/** Gemensam kropp: vit särk, vida ärmar, huvud med hår och ansikte. */
const Body: React.FC<{
  p: Palette;
  look: Look;
  sash?: ShadeColors;
  /** Struten täcker hjässan. */
  hat?: boolean;
  /** Ljuset som lyser upp särken framifrån (y). */
  glowY?: number;
  children?: React.ReactNode;
  /** Ritas efter håret (t.ex. glitter eller krona). */
  crown?: React.ReactNode;
  /** Ritas sist, framför allt annat (t.ex. ett ljus som hålls upp). */
  front?: React.ReactNode;
}> = ({ p, look, sash, hat, glowY = -90, children, crown, front }) => {
  const sk = SKINS[look.skin];
  const g = gown(p);
  return (
    <g>
      <Contact cx={0} cy={0} rx={62} ry={8} color="#06082a" opacity={0.42} />
      <BackHair look={look} />
      {/* Särken */}
      <Shaded
        d="M-28 -120 C -40 -82, -52 -32, -58 0 L 58 0 C 52 -32, 40 -82, 28 -120 C 18 -126, -18 -126, -28 -120 Z"
        c={g}
        depth={14}
        highlight={{ cx: -16, cy: -88, rx: 7, ry: 22, rot: 10, opacity: 0.7 }}
      >
        <g stroke={g.shade} strokeWidth="1.6" fill="none" opacity="0.8" strokeLinecap="round">
          <path d="M-18 -70 C -24 -40, -30 -18, -34 -2" />
          <path d="M4 -76 C 4 -46, 6 -22, 6 -2" />
          <path d="M20 -70 C 26 -44, 30 -20, 34 -2" />
        </g>
        {/* Varmt ljus från lågan på särkens framsida */}
        <ellipse cx={TURN} cy={glowY + 14} rx={44} ry={56} fill="url(#lu-glow)" opacity="0.9" />
      </Shaded>
      {sash && (
        <g>
          <Shaded d="M-30 -104 C -10 -97, 10 -97, 30 -104 L 32 -93 C 10 -86, -10 -86, -32 -93 Z" c={sash} depth={3} />
          <Shaded d="M-20 -94 C -24 -76, -22 -56, -28 -40 L -18 -38 C -14 -56, -14 -76, -12 -92 Z" c={sash} depth={3} />
          <Shaded d="M-12 -92 C -10 -74, -6 -58, -8 -46 L 0 -48 C 0 -62, -2 -78, -4 -92 Z" c={sash} depth={2} />
        </g>
      )}
      {/* Hals och rund krage */}
      <rect x={-6} y={-128} width={12} height={12} fill={sk.shade} />
      <path d="M-15 -121 C -8 -113, 8 -113, 15 -121" stroke={g.shade} strokeWidth="2" fill="none" strokeLinecap="round" />
      {/* Vida ärmar som möts framför bröstet */}
      <Shaded d="M-27 -118 C -42 -102, -42 -80, -30 -70 L -4 -80 C -10 -94, -16 -108, -22 -118 Z" c={g} depth={5} />
      <Shaded d="M27 -118 C 42 -102, 42 -80, 30 -70 L 4 -80 C 10 -94, 16 -108, 22 -118 Z" c={g} depth={5} />
      {children}
      {/* Öron och huvud, med lågans sken på hakan och kinderna */}
      {!(look.style === "long" || look.style === "bangs") &&
        [-1, 1].map(side => <Sphere key={side} cx={side * 19.6 + TURN * 0.4} cy={-140} r={4.2} c={sk} />)}
      <Sphere cx={0} cy={-142} r={20} c={sk}>
        <ellipse cx={TURN} cy={-124} rx={18} ry={11} fill="#ffb347" opacity="0.28" filter="url(#toon-softer)" />
      </Sphere>
      <Face look={look} brows={!hat && look.style !== "bangs"} />
      <FrontHair look={look} />
      {crown}
      {front}
    </g>
  );
};

/** Hopknäppta händer. */
const Hands: React.FC<{ look: Look }> = ({ look }) => (
  <g>
    <Sphere cx={-4} cy={-82} r={5} c={SKINS[look.skin]} />
    <Sphere cx={4} cy={-82} r={5} c={SKINS[look.skin]} />
  </g>
);

/** Steg och vaggning när tåget går. */
const Walk: React.FC<{ x: number; y: number; s: number; delay: number; children: React.ReactNode }> = ({ x, y, s, delay, children }) => (
  <g transform={`translate(${x} ${y}) scale(${s})`}>
    <g className="hol-procession" style={{ animationDelay: `${delay}s` }}>
      <g className="hol-step" style={{ animationDelay: `${delay * 0.7}s` }}>
        {children}
      </g>
    </g>
  </g>
);

/** Lucia med ljuskrona av lingonris och röd skärp. */
export const Lucia: React.FC<ChildProps> = ({ x, y, s = 1, p, look, delay = 0 }) => {
  const crown = range(7).map(i => {
    const a = Math.PI + (i / 6) * Math.PI;
    return { x: Math.cos(a) * 19, y: -160 + Math.sin(a) * 4.5 };
  });
  return (
    <Walk x={x} y={y} s={s} delay={delay}>
      {/* Ljuset från kronan faller över henne */}
      <circle cx="0" cy="-176" r="130" fill="url(#lu-glow)" className="hol-flicker" style={{ animationDelay: `${delay}s` }} />
      <Body
        p={p}
        look={look}
        sash={SASH}
        glowY={-150}
        crown={
          <g>
            <ellipse cx="0" cy="-160" rx="22" ry="6" fill={LEAF.shade} />
            {crown.map((c, i) => (
              <Candle key={i} x={c.x} y={c.y} h={i === 3 ? 26 : i === 2 || i === 4 ? 23 : 20} w={5} delay={delay - i * 0.4} halo={20} />
            ))}
            {range(18).map(i => {
              const a = (i / 18) * Math.PI * 2;
              const lx = Math.cos(a) * 21.5;
              const ly = -158 + Math.sin(a) * 5.6;
              return (
                <ellipse
                  key={i}
                  cx={lx}
                  cy={ly}
                  rx="4.8"
                  ry="2.4"
                  transform={`rotate(${(a * 180) / Math.PI + 90 + (i % 2 ? 30 : -30)} ${lx} ${ly})`}
                  fill={Math.sin(a) > 0 ? (i % 3 ? LEAF.base : LEAF.light) : LEAF.shade}
                />
              );
            })}
            {[-17, -9, -1, 8, 16].map((bx, i) => (
              <g key={i}>
                <circle cx={bx} cy={-155.6 + (i % 2)} r="1.9" fill="#d9243a" />
                <circle cx={bx - 0.6} cy={-156.2 + (i % 2)} r="0.6" fill="#ffb0b8" />
              </g>
            ))}
          </g>
        }
      >
        <Hands look={look} />
      </Body>
    </Walk>
  );
};

/** Tärna med glitter i håret och ett ljus med droppskydd i händerna. */
export const Attendant: React.FC<ChildProps> = ({ x, y, s = 1, p, look, delay = 0 }) => (
  <Walk x={x} y={y} s={s} delay={delay}>
    <circle cx="0" cy="-118" r="100" fill="url(#lu-glow)" className="hol-flicker" style={{ animationDelay: `${delay}s` }} opacity="0.85" />
    <Body
      p={p}
      look={look}
      glowY={-104}
      crown={<Tinsel y={look.style === "puffs" ? -160 : look.style === "bangs" ? -164 : -158} w={look.style === "puffs" ? 19 : 20} />}
      front={
        <g>
          <circle cx="0" cy="-104" r="22" fill="#ffc46a" opacity="0.18" filter="url(#toon-soft)" />
          <Candle x={0} y={-80} h={26} w={6.5} delay={delay} halo={30} />
          <ellipse cx="0" cy="-80" rx="11" ry="3" fill="#dfe3f7" />
          <ellipse cx="0" cy="-81" rx="9" ry="2" fill="#ffffff" />
          <Hands look={look} />
        </g>
      }
    >
      {/* Glitterbälte */}
      <path d="M-31 -98 C -10 -91, 10 -91, 31 -98" stroke="#dfe4ff" strokeWidth="4" fill="none" strokeLinecap="round" />
      <path d="M-31 -98 C -10 -91, 10 -91, 31 -98" stroke="#ffffff" strokeWidth="1.4" strokeDasharray="1 3" fill="none" className="hol-star-group" />
    </Body>
  </Walk>
);

const starPts = (cx: number, cy: number, r: number) =>
  range(10)
    .map(i => {
      const rr = i % 2 ? r * 0.45 : r;
      const a = (Math.PI / 5) * i - Math.PI / 2;
      return `${(cx + Math.cos(a) * rr).toFixed(1)},${(cy + Math.sin(a) * rr).toFixed(1)}`;
    })
    .join(" ");

/** Stjärngosse med strut och stjärnstav. */
export const StarBoy: React.FC<ChildProps> = ({ x, y, s = 1, p, look, delay = 0 }) => {
  const hat: ShadeColors = { base: "#ffffff", shade: mix("#dfe3f8", p[300], 0.3), light: "#ffffff" };
  const sk = SKINS[look.skin];
  return (
    <Walk x={x} y={y} s={s} delay={delay}>
      <Body
        p={p}
        look={look}
        hat
        glowY={-150}
        crown={
          <g>
            {/* Struten */}
            <Shaded d="M-22 -149 L 2 -236 L 22 -149 C 12 -145, -12 -145, -22 -149 Z" c={hat} depth={6} highlight={{ cx: -6, cy: -196, rx: 3, ry: 16, rot: 14, opacity: 0.8 }} />
            <path d="M-22 -149 C -12 -145, 12 -145, 22 -149" stroke={hat.shade} strokeWidth="1.6" fill="none" />
            {[
              [-6, -170, 5],
              [7, -190, 4],
              [-1, -212, 3.4],
              [10, -164, 3],
            ].map(([sx, sy, sr], i) => (
              <polygon key={i} points={starPts(sx, sy, sr)} fill={GOLD.base} />
            ))}
            <circle cx="2" cy="-238" r="4" fill={GOLD.base} />
          </g>
        }
      >
        {/* Stjärnstaven */}
        <path d="M22 -80 L 34 -196" stroke="#c9ceea" strokeWidth="3" strokeLinecap="round" />
        <circle cx="34" cy="-200" r="34" fill="url(#lu-glow)" className="hol-breathe" />
        <Shaded d={`M${starPts(34, -200, 13).split(" ").join(" L")} Z`.replace(/,/g, " ")} c={GOLD} depth={3} />
        <Sphere cx={20} cy={-80} r={5} c={sk} />
        <Sphere cx={-6} cy={-82} r={5} c={sk} />
      </Body>
    </Walk>
  );
};

/** Adventsstjärna i papper som lyser inifrån. */
export const PaperStar: React.FC<{ thread: number }> = ({ thread }) => {
  const pts = (r: number, inner: number) =>
    range(10)
      .map(i => {
        const rr = i % 2 ? inner : r;
        const a = (Math.PI / 5) * i - Math.PI / 2;
        return `${(Math.cos(a) * rr).toFixed(1)},${(thread + 46 + Math.sin(a) * rr).toFixed(1)}`;
      })
      .join(" ");
  return (
    <svg viewBox={`-50 0 100 ${thread + 96}`}>
      <line x1="0" y1="0" x2="0" y2={thread + 4} stroke="#c7cdf0" strokeOpacity="0.55" strokeWidth="1" />
      <circle cx="0" cy={thread + 46} r="50" fill="url(#lu-glow)" className="hol-breathe" />
      <polygon points={pts(44, 20)} fill="url(#lu-paper)" />
      {/* Veck mellan uddarna ger 3D-känsla */}
      {range(5).map(i => {
        const a = (Math.PI / 5) * i * 2 - Math.PI / 2;
        return <line key={i} x1="0" y1={thread + 46} x2={(Math.cos(a) * 44).toFixed(1)} y2={(thread + 46 + Math.sin(a) * 44).toFixed(1)} stroke="#e8b46a" strokeOpacity="0.55" strokeWidth="1.2" />;
      })}
      {range(5).map(i => {
        const a = (Math.PI / 5) * (i * 2 + 1) - Math.PI / 2;
        return <line key={`v${i}`} x1="0" y1={thread + 46} x2={(Math.cos(a) * 20).toFixed(1)} y2={(thread + 46 + Math.sin(a) * 20).toFixed(1)} stroke="#ffffff" strokeOpacity="0.6" strokeWidth="1" />;
      })}
      {/* Perforerat mönster */}
      {range(10).map(i => {
        const a = (Math.PI / 5) * i - Math.PI / 2;
        return <circle key={`h${i}`} cx={(Math.cos(a) * 12).toFixed(1)} cy={(thread + 46 + Math.sin(a) * 12).toFixed(1)} r="1.6" fill="#fff8e6" />;
      })}
      <circle cx="0" cy={thread + 46} r="4" fill="#ffffff" />
    </svg>
  );
};

/** Gemensamma gradienter för luciascenen. */
export const LuciaDefs: React.FC = () => (
  <svg width="0" height="0" style={{ position: "absolute" }} aria-hidden="true">
    <defs>
      <radialGradient id="lu-flame" cx="50%" cy="75%" r="65%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="35%" stopColor="#fff0a0" />
        <stop offset="100%" stopColor="#ff9a2a" />
      </radialGradient>
      <radialGradient id="lu-halo" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#ffe6a0" stopOpacity="0.75" />
        <stop offset="45%" stopColor="#ffc46a" stopOpacity="0.22" />
        <stop offset="100%" stopColor="#ffb04a" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="lu-glow" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#ffd98a" stopOpacity="0.5" />
        <stop offset="55%" stopColor="#ffb85a" stopOpacity="0.1" />
        <stop offset="100%" stopColor="#ffb04a" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="lu-paper" cx="50%" cy="50%" r="55%">
        <stop offset="0%" stopColor="#ffffff" />
        <stop offset="40%" stopColor="#fff4d8" />
        <stop offset="100%" stopColor="#f5c77e" />
      </radialGradient>
      <radialGradient id="lu-window" cx="50%" cy="50%" r="50%">
        <stop offset="0%" stopColor="#ffe3a0" stopOpacity="0.8" />
        <stop offset="100%" stopColor="#ffb85a" stopOpacity="0" />
      </radialGradient>
    </defs>
  </svg>
);
